import React, { useState } from "react";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useDefectCreation } from "../../hooks/useDefectCreation";
import RenderFields from "../../../../../../../components/Modals/ModalFields/RenderFields";
import { getDefectFields } from "../../config/defectConfig";
import { useHeaders } from "../../HeadersContext";
import { Alert, CircularProgress, Box } from "@mui/material";

const DefectCreationStep = ({
  selectedDevices,
  elv3Guid,
  onSubmit,
  testMode = false,
  initialDefect = null,
}) => {
  const { headers } = useHeaders() || {};
  const [submitError, setSubmitError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    defects,
    devicePartOptions,
    violatingConditionOptions,
    suggestedRemedyOptions,
    isLoading,
    error,
    submissionStatus,
    handleAddDefect,
    handleRemoveDefect,
    handleDefectChange,
    submitDefects,
    isValid,
    hasValidDefects,
  } = useDefectCreation({
    devices: testMode ? mockDevices : selectedDevices,
    elv3Guid,
    initialDefect,
  });

  const handleFieldChange = (deviceId, index) => (e) => {
    handleDefectChange(deviceId, index, e.target.name, e.target.value);
  };

  const handleSubmission = async () => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);

      if (!headers && !testMode) {
        throw new Error(
          "Authorization headers not available. Cannot submit defects."
        );
      }

      if (!hasValidDefects()) {
        throw new Error(
          "No valid defects to submit. Please fill all required fields."
        );
      }

      if (testMode) {
        console.log("Test mode active - Would submit defects:", defects);
        setTimeout(() => {
          setIsSubmitting(false);
          onSubmit(defects);
        }, 1000);
        return;
      }

      const result = await submitDefects(headers);
      onSubmit(result);
    } catch (error) {
      console.error("Error submitting defects:", error);
      setSubmitError(error.message || "Failed to submit defects");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <CircularProgress />
        <span className="ml-2">Loading options...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 text-red-700 p-4 rounded-md">{error}</div>
    );
  }

  if (
    !devicePartOptions ||
    !violatingConditionOptions ||
    !suggestedRemedyOptions
  ) {
    return (
      <div className="flex justify-center items-center p-8">
        <CircularProgress />
        <span className="ml-2">Loading dropdown options...</span>
      </div>
    );
  }

  const devices = testMode ? mockDevices : selectedDevices;

  return (
    <div className="p-8 bg-white">
      {testMode && (
        <div className="mb-4 bg-blue-100 text-blue-700 p-4 rounded-md">
          Test Mode Active - No data will be submitted to the server
        </div>
      )}

      {elv3Guid && (
        <div className="mb-4 bg-green-100 text-green-700 p-4 rounded-md">
          ELV3 Group GUID: {elv3Guid}
        </div>
      )}

      {submitError && (
        <Alert severity="error" className="mb-4">
          {submitError}
        </Alert>
      )}

      {submissionStatus && (
        <Alert
          severity={
            submissionStatus.status === "error"
              ? "error"
              : submissionStatus.status === "success"
              ? "success"
              : "info"
          }
          className="mb-4"
        >
          {submissionStatus.message}
        </Alert>
      )}

      <h2 className="text-2xl font-semibold mb-2">
        {initialDefect ? "Edit Defect" : "Create New Defect"}
      </h2>

      {devices.map((device) => (
        <div key={device.DeviceID} className="border p-4 m-4 shadow-lg">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-xl font-medium">
                Device ID: {device.DeviceID}
              </h3>
              <p className="text-sm text-gray-600">
                Type: {device.DeviceTypeLabel}
              </p>
            </div>
            {!initialDefect && (
              <button
                onClick={() =>
                  handleAddDefect(device.DeviceID, device.DeviceTypeLabel)
                }
                className="px-2 py-1 bg-gray-700 text-white hover:bg-gray-500 hover:ring-teal-100 transition "
              >
                Add Defect to {device.DeviceName || device.DeviceID}
              </button>
            )}
          </div>

          {defects[device.DeviceID]?.map((defect, index) => (
            <div key={index} className="my-4 border px-2 shadow-sm bg-gray-50">
              <div className="flex justify-between items-center border-b border-gray-300 py-1">
                <h4 className="text-md font-medium">Defect {index + 1}</h4>
                {!initialDefect && (
                  <button
                    onClick={() => handleRemoveDefect(device.DeviceID, index)}
                    className="p-2 text-gray-800 hover:text-red-500 hover:bg-gray-300 rounded-full"
                  >
                    <DeleteIcon />
                  </button>
                )}
              </div>

              <div className="flex flex-col space-y-4 my-2">
                <div className="grid grid-cols-3 gap-4">
                  <RenderFields
                    fields={getDefectFields(
                      devicePartOptions || [],
                      violatingConditionOptions || [],
                      suggestedRemedyOptions || []
                    )}
                    formData={defect || {}}
                    handleChange={handleFieldChange(device.DeviceID, index)}
                    readOnly={initialDefect !== null}
                  />
                </div>

                <div className="w-full">
                  <RenderFields
                    fields={[
                      {
                        key: "comments",
                        label: "Comments",
                        type: "textArea",
                        rows: 2,
                      },
                    ]}
                    formData={defect || {}}
                    handleChange={handleFieldChange(device.DeviceID, index)}
                    readOnly={initialDefect !== null}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      ))}

      <div className="mt-6 flex justify-end">
        {isSubmitting ? (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <CircularProgress size={24} sx={{ marginRight: 1 }} />
            <span>Submitting defects...</span>
          </Box>
        ) : (
          <button
            onClick={handleSubmission}
            disabled={
              isSubmitting ||
              Object.values(defects).every(
                (deviceDefects) =>
                  deviceDefects.length === 0 || !deviceDefects.some(isValid)
              )
            }
            className="bg-green-600 text-white px-4 py-2 hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {testMode
              ? "Test Submit"
              : initialDefect
              ? "Update Defect"
              : "Submit Defects"}
          </button>
        )}
      </div>
    </div>
  );
};

export default DefectCreationStep;

// Mock data for testing
const mockDevices = [
  {
    DeviceID: "123456",
    DeviceTypeLabel: "Elevator",
    InspectionType: "Category 1",
  },
  {
    DeviceID: "789012",
    DeviceTypeLabel: "Escalator",
    InspectionType: "Category 3",
  },
];
